// src/components/AuthModal.js
import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuthContext } from '../../context/AuthContext';
import PasswordInput from './PasswordInput';
import apiClient from '../../api/apiClient';

// توابع کمکی تبدیل اعداد فارسی ↔ انگلیسی
const persianToEnglishMap = {
  '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
  '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9'
};
const englishToPersianMap = {
  '0': '۰', '1': '۱', '2': '۲', '3': '۳', '4': '۴',
  '5': '۵', '6': '۶', '7': '۷', '8': '۸', '9': '۹'
};

const convertPersianToEnglish = (str) => {
  if (!str) return '';
  return str.replace(/[۰-۹]/g, (ch) => persianToEnglishMap[ch] || ch);
};

const convertEnglishToPersian = (str) => {
  if (!str) return '';
  return str.replace(/[0-9]/g, (ch) => englishToPersianMap[ch] || ch);
};

export default function AuthModal() {
  const { login, register, loginWithToken, isModalOpen, closeAuthModal } = useAuthContext();

  const [mode, setMode] = useState('login');                 // 'login', 'register', 'reset'
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' | 'otp'
  const [resetStep, setResetStep] = useState('request');      // 'request' | 'confirm'
  const [otpStep, setOtpStep] = useState('idle');             // 'idle' | 'sent'

  // state های اصلی (انگلیسی)
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // state نمایشی برای فیلد شناسه (فارسی)
  const [displayIdentifier, setDisplayIdentifier] = useState('');

  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    setApiError(null);
  }, [mode, resetStep, loginMethod, otpStep]);

  if (!isModalOpen) return null;

  // ── نرمال‌سازی شماره موبایل (خروجی انگلیسی) ──
  const normalizePhoneNumber = (input) => {
    let normalized = convertPersianToEnglish(input);
    normalized = normalized.replace(/\D/g, '');
    if (normalized.startsWith('98') && normalized.length === 12) {
      normalized = '0' + normalized.slice(2);
    }
    if (normalized.startsWith('9') && normalized.length === 10) {
      normalized = '0' + normalized;
    }
    return normalized;
  };

  const getIdentifierType = (value) => {
    const trimmed = value.trim();
    if (trimmed.includes('@')) return 'email';
    const phone = normalizePhoneNumber(trimmed);
    if (phone.length === 11 && phone.startsWith('09')) return 'phone';
    return 'unknown';
  };

  // ── مدیریت تغییر فیلد شناسه ──
  const handleIdentifierChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.includes('@')) {
      setIdentifier(rawValue);
      setDisplayIdentifier(rawValue);
    } else {
      const englishDigits = rawValue.replace(/[۰-۹]/g, (ch) => persianToEnglishMap[ch] || ch);
      setIdentifier(englishDigits);
      setDisplayIdentifier(convertEnglishToPersian(englishDigits));
    }
  };

  // ── ورود OTP (اصلاح‌شده: تشخیص عدم وجود کاربر) ──
  const handleOtpRequest = async (e) => {
    e.preventDefault();
    const cleanId = identifier.trim();

    // 🔍 لاگ برای بررسی مقدار ارسالی
    console.log('🔵 [Frontend] handleOtpRequest - identifier before normalization:', cleanId);
    console.log('🔵 [Frontend] identifier type:', getIdentifierType(cleanId));

    if (!cleanId) {
      setApiError('لطفاً ایمیل یا شماره موبایل خود را وارد کنید.');
      return;
    }
    let finalId = cleanId;
    if (getIdentifierType(cleanId) === 'phone') {
      finalId = normalizePhoneNumber(cleanId);
      console.log('🔵 [Frontend] normalized phone number:', finalId);
    } else {
      console.log('🔵 [Frontend] email (no change):', finalId);
    }
    setLocalIsLoading(true);
    setApiError(null);
    try {
      console.log('🔵 [Frontend] sending to backend:', { identifier: finalId });
      await apiClient.post('/auth/otp/request/', { identifier: finalId });
      setOtpStep('sent');
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error || err.response?.data?.message || '';
      // ✅ اگر کاربر وجود نداشت → پیام ثبت‌نام
      if (status === 404 || serverMsg.includes('یافت نشد') || serverMsg.includes('وجود ندارد')) {
        setApiError('کاربری با این مشخصات یافت نشد. لطفاً ابتدا ثبت‌نام کنید.');
      } else {
        setApiError(serverMsg || err.message || 'خطا در ارسال کد');
      }
    } finally {
      setLocalIsLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    const cleanId = identifier.trim();
    const code = otpCode.trim();

    // 🔍 لاگ برای بررسی مقدار ارسالی در تأیید
    console.log('🔵 [Frontend] handleOtpVerify - identifier:', cleanId);
    console.log('🔵 [Frontend] handleOtpVerify - otp_code (raw):', code);

    if (!cleanId || !code) {
      setApiError('لطفاً کد دریافتی را وارد کنید.');
      return;
    }
    let finalId = cleanId;
    if (getIdentifierType(cleanId) === 'phone') {
      finalId = normalizePhoneNumber(cleanId);
    }
    const englishCode = convertPersianToEnglish(code);
    setLocalIsLoading(true);
    setApiError(null);
    try {
      console.log('🔵 [Frontend] sending verify to backend:', { identifier: finalId, otp_code: englishCode });
      const res = await apiClient.post('/auth/otp/verify/', {
        identifier: finalId,
        otp_code: englishCode,
      });
      loginWithToken(res.data.access, res.data.refresh, res.data.user_info);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'کد نامعتبر است';
      setApiError(msg);
    } finally {
      setLocalIsLoading(false);
    }
  };

  // ── توابع ریست پسورد (بدون تغییر) ──
  const handleResetRequest = async (e) => {
    e.preventDefault();
    const rawIdentifier = identifier.trim();
    if (!rawIdentifier) {
      setApiError('لطفاً ایمیل یا شماره موبایل خود را وارد کنید.');
      return;
    }
    let finalIdentifier = rawIdentifier;
    if (getIdentifierType(rawIdentifier) === 'phone') {
      finalIdentifier = normalizePhoneNumber(rawIdentifier);
    }
    setLocalIsLoading(true);
    setApiError(null);
    try {
      await apiClient.post('/auth/password-reset/request/', { identifier: finalIdentifier });
      setResetStep('confirm');
      setApiError(null);
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data?.error;
      setApiError(serverMsg || err.message || 'درخواست با خطا مواجه شد.');
      if (getIdentifierType(rawIdentifier) === 'email') {
        setApiError(prev => prev + ' (در صورت ثبت‌نام با شماره موبایل، لطفاً شماره خود را وارد کنید)');
      }
    } finally {
      setLocalIsLoading(false);
    }
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    const rawIdentifier = identifier.trim();
    if (!rawIdentifier) {
      setApiError('شناسه یافت نشد.');
      return;
    }
    if (!otpCode.trim()) {
      setApiError('لطفاً کد تأیید را وارد کنید.');
      return;
    }
    if (!newPassword) {
      setApiError('رمز عبور جدید را وارد کنید.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setApiError('رمز عبور جدید و تکرار آن مطابقت ندارند.');
      return;
    }
    if (newPassword.length < 6) {
      setApiError('رمز عبور باید حداقل ۶ کاراکتر باشد.');
      return;
    }

    let finalIdentifier = rawIdentifier;
    if (getIdentifierType(rawIdentifier) === 'phone') {
      finalIdentifier = normalizePhoneNumber(rawIdentifier);
    }

    const otpToSend = otpCode; // انگلیسی

    setLocalIsLoading(true);
    setApiError(null);
    try {
      await apiClient.post('/auth/password-reset/confirm/', {
        identifier: finalIdentifier,
        otp_code: otpToSend,
        password: newPassword,
        password2: confirmPassword,
      });
      setResetSuccess(true);
      setTimeout(() => {
        setMode('login');
        setResetStep('request');
        setResetSuccess(false);
        setOtpCode('');
        setNewPassword('');
        setConfirmPassword('');
        setIdentifier('');
        setDisplayIdentifier('');
        setApiError(null);
      }, 2000);
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data?.error;
      setApiError(serverMsg || err.message || 'تأیید کد با خطا مواجه شد.');
    } finally {
      setLocalIsLoading(false);
    }
  };

  const goBackToLogin = () => {
    setMode('login');
    setResetStep('request');
    setApiError(null);
    setResetSuccess(false);
    setOtpCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleClose = () => {
    setApiError(null);
    setRegisterSuccess(false);
    setResetSuccess(false);
    setIdentifier('');
    setDisplayIdentifier('');
    setPassword('');
    setFullName('');
    setOtpCode('');
    setNewPassword('');
    setConfirmPassword('');
    setMode('login');
    setLoginMethod('password');
    setOtpStep('idle');
    setResetStep('request');
    closeAuthModal();
  };

  // ── تابع submit اصلی (ورود با رمز عبور / ثبت‌نام) ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    const rawIdentifier = identifier.trim();
    const cleanFullName = fullName.trim();

    // 🔍 لاگ برای بررسی مقدار ارسالی در لاگین با رمز عبور
    console.log('🔵 [Frontend] handleSubmit - rawIdentifier:', rawIdentifier);
    console.log('🔵 [Frontend] identifier type:', getIdentifierType(rawIdentifier));

    if (!rawIdentifier || !password || (mode === 'register' && !cleanFullName)) {
      setApiError("لطفاً تمام فیلدها را به درستی تکمیل کنید.");
      return;
    }

    // نرمال‌سازی شماره تلفن برای لاگین و ثبت‌نام
    let finalIdentifier = rawIdentifier;
    if (getIdentifierType(rawIdentifier) === 'phone') {
      finalIdentifier = normalizePhoneNumber(rawIdentifier);
      console.log('🔵 [Frontend] normalized phone number:', finalIdentifier);
    } else {
      console.log('🔵 [Frontend] email (no change):', finalIdentifier);
    }

    setLocalIsLoading(true);
    setApiError(null);
    try {
      if (mode === 'login') {
        console.log('🔵 [Frontend] login with:', { identifier: finalIdentifier });
        await login(finalIdentifier, password, false);
      } else {
        const isEmail = rawIdentifier.includes('@');
        const phone = isEmail ? null : finalIdentifier;
        const email = isEmail ? rawIdentifier : null;
        console.log('🔵 [Frontend] register with:', { phone, email });
        await register(phone, email, password, cleanFullName);
        setRegisterSuccess(true);
        setMode('login');
        setPassword('');
        setApiError(null);
      }
    } catch (err) {
      setApiError(err.message || "خطایی در سیستم رخ داده است.");
    } finally {
      setLocalIsLoading(false);
    }
  };

  // ── رندر زیرفرم‌ها ──

  const renderOtpLogin = () => (
    <form onSubmit={otpStep === 'idle' ? handleOtpRequest : handleOtpVerify} className="space-y-5">
      <div className="space-y-1.5">
        <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">ایمیل یا شماره موبایل</label>
        <input
          type="text"
          value={displayIdentifier}
          onChange={handleIdentifierChange}
          required
          placeholder="شماره موبایل یا ایمیل"
          className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-left bg-gray-50/50"
          dir="ltr"
          disabled={otpStep !== 'idle'}
        />
      </div>

      {otpStep === 'sent' && (
        <div className="space-y-1.5">
          <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">کد تأیید</label>
          <input
            type="text"
            value={convertEnglishToPersian(otpCode)}
            onChange={(e) => setOtpCode(convertPersianToEnglish(e.target.value))}
            required
            placeholder="کد ۶ رقمی"
            className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-left bg-gray-50/50"
            dir="ltr"
          />
        </div>
      )}

      {apiError && (
        <div className="flex items-start gap-3 p-4 text-[11px] text-red-700 bg-red-50 border-r-4 border-red-500 rounded-xl">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="font-medium leading-relaxed text-right">
            {apiError}
            {/* ✅ دکمهٔ ثبت‌نام در صورت نبود کاربر */}
            {apiError.includes('ثبت‌نام کنید') && (
              <button
                type="button"
                onClick={() => {
                  setMode('register');
                  setLoginMethod('password');
                  setApiError(null);
                  setOtpStep('idle');
                }}
                className="block mt-2 text-xs font-bold text-purple-700 hover:text-purple-900 underline transition-colors"
              >
                برو به ثبت‌نام
              </button>
            )}
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={localIsLoading}
        className="w-full py-4 rounded-2xl font-bold text-white bg-purple-700 hover:bg-purple-800 active:scale-[0.98] transition-all disabled:bg-purple-300 shadow-lg shadow-purple-100 flex items-center justify-center"
      >
        {localIsLoading ? <Loader2 size={24} className="animate-spin" /> : (otpStep === 'idle' ? 'ارسال کد تأیید' : 'ورود')}
      </button>

      {otpStep === 'sent' && (
        <button
          type="button"
          onClick={() => setOtpStep('idle')}
          className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
        >
          ← بازگشت و ویرایش شماره
        </button>
      )}
    </form>
  );

  const renderResetRequest = () => (
    <form onSubmit={handleResetRequest} className="space-y-5">
      <div className="space-y-1.5">
        <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">
          ایمیل یا شماره موبایل
        </label>
        <input
          type="text"
          value={displayIdentifier}
          onChange={handleIdentifierChange}
          required
          placeholder="شماره موبایل یا ایمیل"
          className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-left transition-all bg-gray-50/50"
          dir="ltr"
        />
      </div>
      {apiError && (
        <div className="flex items-start gap-3 p-4 text-[11px] text-red-700 bg-red-50 border-r-4 border-red-500 rounded-xl">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <span className="font-medium leading-relaxed text-right">{apiError}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={localIsLoading}
        className="w-full py-4 rounded-2xl font-bold text-white bg-purple-700 hover:bg-purple-800 active:scale-[0.98] transition-all disabled:bg-purple-300 shadow-lg shadow-purple-100 flex items-center justify-center"
      >
        {localIsLoading ? <Loader2 size={24} className="animate-spin" /> : 'ارسال کد تأیید'}
      </button>
      <button
        type="button"
        onClick={goBackToLogin}
        className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
      >
        ← بازگشت به صفحه ورود
      </button>
    </form>
  );

  const renderResetConfirm = () => (
    <form onSubmit={handleResetConfirm} className="space-y-5">
      <div className="space-y-1.5">
        <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">کد تأیید</label>
        <input
          type="text"
          value={convertEnglishToPersian(otpCode)}
          onChange={(e) => setOtpCode(convertPersianToEnglish(e.target.value))}
          required
          placeholder="کد احراز هویت"
          className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-left bg-gray-50/50"
          dir="ltr"
        />
      </div>
      <div className="space-y-1.5">
        <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">رمز عبور جدید</label>
        <PasswordInput
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="••••••••"
          showValidation={false}
        />
      </div>
      <div className="space-y-1.5">
        <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">تکرار رمز عبور جدید</label>
        <PasswordInput
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          showValidation={false}
        />
      </div>
      {resetSuccess && (
        <div className="flex items-center gap-3 p-4 text-[11px] text-green-800 bg-green-50 border-r-4 border-green-500 rounded-xl">
          <CheckCircle2 size={20} className="text-green-600 flex-shrink-0" />
          <span className="font-medium">رمز عبور با موفقیت تغییر کرد. به صفحه ورود هدایت می‌شوید...</span>
        </div>
      )}
      {apiError && (
        <div className="flex items-start gap-3 p-4 text-[11px] text-red-700 bg-red-50 border-r-4 border-red-500 rounded-xl">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <span className="font-medium leading-relaxed text-right">{apiError}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={localIsLoading || resetSuccess}
        className="w-full py-4 rounded-2xl font-bold text-white bg-purple-700 hover:bg-purple-800 active:scale-[0.98] transition-all disabled:bg-purple-300 shadow-lg shadow-purple-100 flex items-center justify-center"
      >
        {localIsLoading ? <Loader2 size={24} className="animate-spin" /> : 'تغییر رمز عبور'}
      </button>
      <button
        type="button"
        onClick={() => {
          setResetStep('request');
          setApiError(null);
          setOtpCode('');
          setNewPassword('');
          setConfirmPassword('');
        }}
        className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
      >
        ← درخواست مجدد کد
      </button>
    </form>
  );

  const renderAuthForm = () => (
    <form onSubmit={handleSubmit} className="space-y-5">
      {registerSuccess && (
        <div className="flex items-center gap-3 p-4 text-[11px] text-green-800 bg-green-50 border-r-4 border-green-500 rounded-xl">
          <CheckCircle2 size={20} className="text-green-600 flex-shrink-0" />
          <span className="font-medium">ثبت‌نام موفق! حالا می‌توانید وارد شوید.</span>
        </div>
      )}

      <div className="space-y-1.5">
        <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">ایمیل یا شماره تماس</label>
        <input
          type="text"
          value={displayIdentifier}
          onChange={handleIdentifierChange}
          required
          placeholder="شماره موبایل یا ایمیل"
          className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-left transition-all bg-gray-50/50"
          dir="ltr"
        />
      </div>

      {mode === 'register' && (
        <div className="space-y-1.5 animate-in fade-in slide-in-from-right-2 duration-300">
          <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">نام و نام خانوادگی</label>
          <input
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            required
            placeholder="مثال: علی محمدی"
            className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-right bg-gray-50/50"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">رمز عبور</label>
        <PasswordInput
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          showValidation={mode === 'register'}
        />
      </div>

      {apiError && (
        <div className="flex items-start gap-3 p-4 text-[11px] text-red-700 bg-red-50 border-r-4 border-red-500 rounded-xl animate-shake">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <span className="font-medium leading-relaxed text-right">{apiError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={localIsLoading}
        className="w-full py-4 rounded-2xl font-bold text-white bg-purple-700 hover:bg-purple-800 active:scale-[0.98] transition-all disabled:bg-purple-300 shadow-lg shadow-purple-100 flex items-center justify-center"
      >
        {localIsLoading ? <Loader2 size={24} className="animate-spin" /> : (mode === 'login' ? 'ورود به حساب' : 'تأیید و ثبت‌نام')}
      </button>

      {mode === 'login' && (
        <button
          type="button"
          onClick={() => {
            setMode('reset');
            setResetStep('request');
            setApiError(null);
            setRegisterSuccess(false);
            setResetSuccess(false);
          }}
          className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
        >
          رمز عبور خود را فراموش کرده‌اید؟
        </button>
      )}

      <button
        type="button"
        onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
        className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
      >
        {mode === 'login' ? 'هنوز عضو نیستید؟ ایجاد حساب کاربری' : 'قبلاً ثبت‌نام کرده‌اید؟ وارد شوید'}
      </button>
    </form>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm rtl px-4"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-md p-8 bg-white rounded-3xl shadow-2xl transition-all scale-100"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-5 left-5 text-gray-400 hover:text-red-500 transition-colors p-1"
        >
          <X size={22} />
        </button>

        <h2 className="text-2xl font-extrabold text-gray-900 text-right mb-8">
          {mode === 'login' && (loginMethod === 'password' ? 'ورود با رمز عبور' : 'ورود سریع')}
          {mode === 'register' && 'ساخت حساب کاربری'}
          {mode === 'reset' && (resetStep === 'request' ? 'بازیابی رمز عبور' : 'تغییر رمز عبور')}
        </h2>

        {/* تب‌های انتخاب روش ورود فقط در حالت login */}
        {mode === 'login' && (
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => { setLoginMethod('password'); setApiError(null); }}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition ${loginMethod === 'password' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
            >
              رمز عبور
            </button>
            <button
              onClick={() => { setLoginMethod('otp'); setApiError(null); setOtpStep('idle'); }}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition ${loginMethod === 'otp' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
            >
              کد یکبار مصرف
            </button>
          </div>
        )}

        {/* محتوای فرم‌ها */}
        {mode === 'reset' ? (
          resetStep === 'request' ? renderResetRequest() : renderResetConfirm()
        ) : mode === 'login' ? (
          loginMethod === 'password' ? renderAuthForm() : renderOtpLogin()
        ) : (
          renderAuthForm() // حالت register
        )}

        {/* دکمه تغییر حالت (فقط در login/register) */}
        {mode !== 'reset' && (
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setLoginMethod('password');
            }}
            className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors mt-4"
          >
            {mode === 'login' ? 'هنوز عضو نیستید؟ ایجاد حساب کاربری' : 'قبلاً ثبت‌نام کرده‌اید؟ وارد شوید'}
          </button>
        )}
      </div>
    </div>
  );
}